import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { compose } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';
import Pficon from 'components/Pficon';
import PfLoader from 'components/PfLoader';
import { createStructuredSelector } from 'reselect';
import classnames from 'classnames';
import { Button, Tooltip } from 'antd';
import questionnaireMessages from 'containers/QuestionnairePage/messages';
import useNavigation from 'containers/QuestionnairePage/GenericQuestion/QuestionFooter/hooks/useNavigation';
import {
  EDIT_PERMISSIONS,
  VISIBILITY_PERMISSIONS,
  NAVIGATION_PERMISSIONS,
  WithPermissions,
} from 'containers/Permissions';
import DropdownActions from 'components/DropdownActions';
import UploadMedia from 'components/UploadMedia';
import { insertActionDividers } from 'components/DropdownMenu/utils';
import UnconditionalHideAnswer from 'containers/QuestionnairePage/UnconditionalHideAnswer';
import UnconditionalHideNode from 'containers/QuestionnairePage/UnconditionalHideNode';
import useUnconditionalHideState from 'containers/QuestionnairePage/UnconditionalHideAnswer/hooks/useStatePropsAndActions';
import FavoriteCheckbox from 'containers/QuestionnairePage/QuestionLibrary/QuestionsSelector/FavoriteCheckbox';
import useMutateFavoriteQuestion from 'containers/QuestionnairePage/QuestionLibrary/hooks/useFavoriteQuestion';
import useAiSurveyBuilder from 'containers/AISurveyBuilder/useAiSurveyBuilder';
import useHasPermission, {
  useHasPermissionWithValues,
} from 'containers/Permissions/hooks/useHasPermission';
import { ActionVisibilityValueType } from 'utils/adl-services';
import useStatePropsAndActionsVAB from 'components/VisibilityActionButton/hooks/useStatePropsAndActions';
import useNavigationLogicState from 'components/NavigationLogicActionButton/hooks/useStatePropsAndActions';
import useGetParentGroup from 'utils/hooks/useGetParentGroup';
import {
  makeSelectActiveQuestionId,
  makeSelectHiddenNodes,
  makeSelectRandomizeQuestionnaireEnabled,
} from 'containers/QuestionnairePage/selectors';
import {
  setActiveNode,
  updateQuestionFavoriteState,
} from 'containers/QuestionnairePage/actions';
import {
  addStatement,
  setScrollToStatementId,
} from 'containers/QuestionnaireLogic/actions';
import { getDefaultStatementWithOutcomeAction } from 'components/LogicOutcomeWithActions/utils';
import { isVisibilityActionButtonDisabled } from 'components/VisibilityActionButton/utils';
import { QUOTA_MODE } from 'data/quotaModes';
import { IMAGE_FILE } from 'components/FileUpload/constants';
import {
  QA_ACTIONS,
  QA_SUPPORTED_ACTIONS,
  ACTIONS_FROM_INPUT_GAP,
  getActionsAreaMinWidth,
  getIndicatorAreaWidth,
  INDICATORS_TO_DROPDOWN_GAP,
} from './constants';
import { QA_ACTION_TYPES } from '../../constants';
import ExclusiveButton from '../ExclusiveButton';
import {
  getAiActionsExcludeList,
  getAvailableDropdownAIActions,
} from '../QuestionAIActions/questionAiActions.utils';
import { buildAiDropdownActions } from '../QuestionAIActions/QuestionAIActionsMenu';
import useMaxDiffExperimentalDesign, {
  GENERATE_DESIGN_STATUS,
} from './hooks/useMaxDiffExperimentalDesign';

const getQuestionnairePopupContainer = () =>
  document.getElementById('js-scrollable-container') || document.body;

const DROPDOWN_SCROLL_SETTLE_MS = 150;
const DROPDOWN_SCROLL_DISMISS_DISTANCE = 400;
const DROPDOWN_SCROLL_DISMISS_ARM_DELAY_MS = 800;
// Activate scroll defers 250ms then animates for 500ms; tall cards can reflow after.
const DROPDOWN_ACTIVATE_SCROLL_MIN_GUARD_MS = 1000;
const DROPDOWN_ACTIVATE_SCROLL_MAX_GUARD_MS = 3000;
const DROPDOWN_MENU_ITEM_HEIGHT = 32;
const DROPDOWN_MENU_VERTICAL_PADDING = 12;
const DROPDOWN_MENU_DIVIDER_HEIGHT = 13;

const estimateQaDropdownMenuHeight = (actions = []) => {
  const menuItems = insertActionDividers(actions);
  let itemCount = 0;
  let dividerCount = 0;

  menuItems.forEach((action) => {
    if (action?.show === false) {
      return;
    }

    if (action?.type === 'divider') {
      dividerCount += 1;
      return;
    }

    itemCount += 1;
  });

  return (
    itemCount * DROPDOWN_MENU_ITEM_HEIGHT +
    dividerCount * DROPDOWN_MENU_DIVIDER_HEIGHT +
    DROPDOWN_MENU_VERTICAL_PADDING
  );
};

// Matches withScrollIntoView focus offset in utils/withScrollIntoView.js
const ACTIVATE_SCROLL_PADDING_Y = 24;
const ACTIVATE_SCROLL_ACTIONS_Y = 30;
const ACTIVATE_SCROLL_MARGIN = 10;
const ACTIVATE_SCROLL_PLUS_ICON_SEMI_HEIGHT = 11;

const getActivateScrollFocusTop = (containerRect) =>
  containerRect.top +
  ACTIVATE_SCROLL_PADDING_Y +
  ACTIVATE_SCROLL_ACTIONS_Y -
  ACTIVATE_SCROLL_MARGIN +
  ACTIVATE_SCROLL_PLUS_ICON_SEMI_HEIGHT;

// Prefer opening down; flip up when the visible scroll viewport can't fit the menu.
const resolveQaDropdownMenuPlacement = (
  triggerEl,
  menuHeight,
  { usePostActivateScrollPosition = false } = {},
) => {
  if (!triggerEl || menuHeight <= 0) {
    return 'bottomRight';
  }

  const scrollContainer = getQuestionnairePopupContainer();
  const containerRect = scrollContainer.getBoundingClientRect();
  const triggerRect = triggerEl.getBoundingClientRect();
  let triggerTop = triggerRect.top;
  let triggerBottom = triggerRect.bottom;

  if (usePostActivateScrollPosition) {
    const questionNode = triggerEl.closest('.questionnaire-node');
    if (!questionNode) {
      return 'bottomRight';
    }

    const questionRect = questionNode.getBoundingClientRect();
    const triggerOffsetFromQuestionTop = triggerRect.top - questionRect.top;
    triggerTop =
      getActivateScrollFocusTop(containerRect) + triggerOffsetFromQuestionTop;
    triggerBottom = triggerTop + triggerRect.height;
  }

  const spaceBelow = containerRect.bottom - triggerBottom;
  const spaceAbove = triggerTop - containerRect.top;

  if (spaceBelow >= menuHeight) {
    return 'bottomRight';
  }

  if (spaceAbove > spaceBelow) {
    return 'topRight';
  }

  return 'bottomRight';
};

const getTriggerSnapshot = (triggerEl) => {
  if (!triggerEl) {
    return null;
  }

  const rect = triggerEl.getBoundingClientRect();
  const scrollContainer = getQuestionnairePopupContainer();
  const containerRect = scrollContainer.getBoundingClientRect();
  const centerY = rect.top + rect.height / 2;
  const viewportCenterY = containerRect.top + containerRect.height / 2;

  return {
    top: rect.top,
    left: rect.left,
    centerY,
    distanceToViewportCenter: Math.abs(centerY - viewportCenterY),
  };
};

const isTriggerInScrollViewport = (triggerEl, scrollContainer) => {
  if (!triggerEl || !scrollContainer) {
    return true;
  }

  const triggerRect = triggerEl.getBoundingClientRect();
  const containerRect = scrollContainer.getBoundingClientRect();

  return (
    triggerRect.bottom > containerRect.top &&
    triggerRect.top < containerRect.bottom &&
    triggerRect.right > containerRect.left &&
    triggerRect.left < containerRect.right
  );
};

// After scroll idles: dismiss when the trigger left the viewport, or moved far
// enough away from its anchor position (not towards it).
const shouldDismissOnScrollAway = (
  anchorSnapshot,
  triggerEl,
  scrollContainer,
) => {
  if (!anchorSnapshot || !triggerEl) {
    return false;
  }

  if (!isTriggerInScrollViewport(triggerEl, scrollContainer)) {
    return true;
  }

  const currentSnapshot = getTriggerSnapshot(triggerEl);
  if (!currentSnapshot) {
    return false;
  }

  const movement = Math.hypot(
    currentSnapshot.left - anchorSnapshot.left,
    currentSnapshot.top - anchorSnapshot.top,
  );

  if (movement < DROPDOWN_SCROLL_DISMISS_DISTANCE) {
    return false;
  }

  return (
    currentSnapshot.distanceToViewportCenter >
    anchorSnapshot.distanceToViewportCenter
  );
};

export const ICON_BOX_STYLE = { width: 16, textAlign: 'center' };
const TOP_LEVEL_ICON_STYLE = { ...ICON_BOX_STYLE, fontSize: 16 };

const SETTINGS_TOOLTIP_BY_VALUE_TYPE = {
  [ActionVisibilityValueType.Node]: questionnaireMessages.questionSettings,
  [ActionVisibilityValueType.Answer]: questionnaireMessages.answerSettings,
  [ActionVisibilityValueType.Row]: questionnaireMessages.statementSettings,
  [ActionVisibilityValueType.Column]: questionnaireMessages.scalePointSettings,
};

const ListItemButton = (props) => {
  const {
    disabled,
    onClick,
    icon,
    iconSize = 12,
    text,
    'data-testid': dataTestId,
    style = {},
  } = props;

  return (
    <Button
      tabIndex="-1"
      data-testid={dataTestId}
      className={classnames(
        'position-relative',
        'text-align-left',
        'border-none',
        'padding-horizontal-xxs',
        'shadow-none',
        'background-transparent',
        'flex flex-items-center flex-justify-between',
        'background-transparent-hover',
        'w-full',
        {
          'color-blue-6-hover': !disabled,
          'color-grey-7 color-grey-7-hover': disabled,
        },
      )}
      disabled={disabled}
      onClick={onClick}
      style={style}
    >
      <span className="flex flex-items-center flex-justify-start">
        {icon && (
          <Pficon
            className={classnames(
              'text-size-secondary',
              'margin-right-xxs',
              'text-align-center',
              {
                'color-grey-7': disabled,
              },
            )}
            style={ICON_BOX_STYLE}
            size={iconSize}
            type={icon}
          />
        )}
        {text}
      </span>
    </Button>
  );
};

const IndicatorWrapper = ({ onClick, tooltip, children }) => {
  const content = (
    <span
      role="button"
      tabIndex={0}
      className="question-answer-action-indicator-slot cursor-pointer"
      onClickCapture={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      onClick={(e) => {
        e.stopPropagation();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {children}
    </span>
  );

  if (!tooltip) {
    return content;
  }

  return (
    <Tooltip
      title={tooltip}
      arrowPointAtCenter
      overlayClassName="tooltip-light"
      placement="bottomRight"
      getPopupContainer={getQuestionnairePopupContainer}
    >
      {content}
    </Tooltip>
  );
};

const QuestionAnswerDropdownActions = (props) => {
  const {
    index,
    supportedActions = QA_SUPPORTED_ACTIONS.ANSWER,
    canReorder = true,
    canReorderAnswerUp,
    canReorderAnswerDown,
    reorderAnswerUp,
    reorderAnswerDown,
    canShowDisplayLogic,
    refId,
    nodeRefId,
    disabled,
    quotaMode = QUOTA_MODE.NONE,
    surveyId,
    currentQuestionRefId,
    supportsExclusiveAnswerUI,
    isExclusive,
    toggleExclusiveAnswer,
    toggleExclusiveScorepoint,
    permissions = {},
    valueType = ActionVisibilityValueType.Answer,
    // media
    media,
    canModifyMedia,
    canUpsertMedia,
    uploadUrl,
    uploadMediaPermission,
    supportedMediaTypes = [IMAGE_FILE],
    onAddMedia,
    onRemoveMedia,
    mediaSlots,
    // question / node remove
    id,
    nodeType,
    isScreening,
    inVanWestendorpGroup,
    isParentGroupNodeHidden,
    onRemoveQuestion,
    onRemoveNode,
    canShowRemove = true,
    removeNodeDisabled = false,
    canDelete,
    removeAnswer,
    canRemove = true,
    removeAnswerMessage: removeAnswerMessageProp,
    removeNodeMessage: removeNodeMessageProp,
    removeNodeIcon = 'close',
    settingsTooltipMessage: settingsTooltipMessageProp,
    // favorite
    isFavorite,
    showFavorite = true,
    registerActionsAreaRef,
    actionsAreaMinWidth = 0,
    indicatorAreaMinWidth = 0,
    isMatrixCell = false,
    isCenteredCell = false,
    reorderDirection = 'vertical',
    style = {},
  } = props;

  const dispatch = useDispatch();
  const activeQuestionId = useSelector(makeSelectActiveQuestionId);
  const randomizeQuestionnaireEnabled = useSelector(
    makeSelectRandomizeQuestionnaireEnabled,
  );
  const parentGroup = useGetParentGroup(
    currentQuestionRefId || nodeRefId || refId,
  );
  const inShuffledGroup =
    Boolean(parentGroup) && parentGroup?.shuffle !== 'NONE';

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [pendingActivateOpen, setPendingActivateOpen] = useState(false);
  const [menuPlacement, setMenuPlacement] = useState('bottomRight');
  const [settingsTooltipVisible, setSettingsTooltipVisible] = useState(false);
  const dropdownTriggerRef = useRef(null);
  const menuHeightEstimateRef = useRef(0);
  const prevMenuPlacementRef = useRef(menuPlacement);
  const menuPlacementRef = useRef(menuPlacement);
  const pendingGuardReleaseRef = useRef(false);
  const scrollDismissAnchorRef = useRef(null);
  const dropdownOpenedAtRef = useRef(0);
  const blockExternalCloseRef = useRef(false);
  const activateScrollStartedAtRef = useRef(0);
  const activateScrollMaxGuardTimeoutRef = useRef(null);
  const activateScrollReleaseTimeoutRef = useRef(null);
  const pendingActivateOpenRef = useRef(false);
  menuPlacementRef.current = menuPlacement;
  pendingActivateOpenRef.current = pendingActivateOpen;
  // const openDropdown = useCallback(() => {
  //   setDropdownOpen(true);
  //   setSettingsTooltipVisible(false);
  // }, []);
  const keepDropdownOpenRef = useRef(false);
  const refreshScrollDismissAnchor = useCallback(() => {
    scrollDismissAnchorRef.current = getTriggerSnapshot(
      dropdownTriggerRef.current,
    );
  }, []);
  const clearActivateScrollGuard = useCallback(() => {
    pendingGuardReleaseRef.current = false;
    pendingActivateOpenRef.current = false;
    setPendingActivateOpen(false);
    blockExternalCloseRef.current = false;
    window.clearTimeout(activateScrollMaxGuardTimeoutRef.current);
    window.clearTimeout(activateScrollReleaseTimeoutRef.current);
    activateScrollMaxGuardTimeoutRef.current = null;
    activateScrollReleaseTimeoutRef.current = null;
  }, []);
  const applyMenuPlacementFromViewport = useCallback(() => {
    setMenuPlacement((current) => {
      const next = resolveQaDropdownMenuPlacement(
        dropdownTriggerRef.current,
        menuHeightEstimateRef.current,
        {
          usePostActivateScrollPosition: blockExternalCloseRef.current,
        },
      );
      return current === next ? current : next;
    });
  }, []);
  const completeActivateScrollGuard = useCallback(() => {
    pendingGuardReleaseRef.current = false;
    blockExternalCloseRef.current = false;
    activateScrollReleaseTimeoutRef.current = null;
    window.clearTimeout(activateScrollMaxGuardTimeoutRef.current);
    activateScrollMaxGuardTimeoutRef.current = null;
    refreshScrollDismissAnchor();
    dropdownOpenedAtRef.current = Date.now();
  }, [refreshScrollDismissAnchor]);
  const openDropdownAfterActivateScroll = useCallback(() => {
    const nextPlacement = resolveQaDropdownMenuPlacement(
      dropdownTriggerRef.current,
      menuHeightEstimateRef.current,
    );

    pendingActivateOpenRef.current = false;
    setPendingActivateOpen(false);
    setMenuPlacement(nextPlacement);
    refreshScrollDismissAnchor();
    dropdownOpenedAtRef.current = Date.now();
    setDropdownOpen(true);
    completeActivateScrollGuard();
    window.requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
  }, [refreshScrollDismissAnchor, completeActivateScrollGuard]);
  const releaseActivateScrollGuard = useCallback(() => {
    if (!blockExternalCloseRef.current) {
      return;
    }

    const elapsed = Date.now() - activateScrollStartedAtRef.current;
    const remainingMinGuard = DROPDOWN_ACTIVATE_SCROLL_MIN_GUARD_MS - elapsed;

    if (remainingMinGuard > 0) {
      window.clearTimeout(activateScrollReleaseTimeoutRef.current);
      activateScrollReleaseTimeoutRef.current = window.setTimeout(
        releaseActivateScrollGuard,
        remainingMinGuard + DROPDOWN_SCROLL_SETTLE_MS,
      );
      return;
    }

    if (pendingActivateOpenRef.current) {
      openDropdownAfterActivateScroll();
      return;
    }

    const nextPlacement = resolveQaDropdownMenuPlacement(
      dropdownTriggerRef.current,
      menuHeightEstimateRef.current,
    );

    if (menuPlacementRef.current !== nextPlacement) {
      pendingGuardReleaseRef.current = true;
      setMenuPlacement(nextPlacement);
      return;
    }

    window.requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
      window.requestAnimationFrame(() => {
        completeActivateScrollGuard();
      });
    });
  }, [completeActivateScrollGuard, openDropdownAfterActivateScroll]);
  const scheduleActivateScrollGuardRelease = useCallback(() => {
    if (!blockExternalCloseRef.current) {
      return;
    }

    window.clearTimeout(activateScrollReleaseTimeoutRef.current);
    activateScrollReleaseTimeoutRef.current = window.setTimeout(
      releaseActivateScrollGuard,
      DROPDOWN_SCROLL_SETTLE_MS,
    );
  }, [releaseActivateScrollGuard]);
  const startActivateScrollGuard = useCallback(() => {
    activateScrollStartedAtRef.current = Date.now();
    blockExternalCloseRef.current = true;
    window.clearTimeout(activateScrollMaxGuardTimeoutRef.current);
    window.clearTimeout(activateScrollReleaseTimeoutRef.current);
    activateScrollMaxGuardTimeoutRef.current = window.setTimeout(
      releaseActivateScrollGuard,
      DROPDOWN_ACTIVATE_SCROLL_MAX_GUARD_MS,
    );
    scheduleActivateScrollGuardRelease();
  }, [releaseActivateScrollGuard, scheduleActivateScrollGuardRelease]);
  const closeDropdown = useCallback(() => {
    clearActivateScrollGuard();
    setDropdownOpen(false);
    setSettingsTooltipVisible(false);
  }, [clearActivateScrollGuard]);
  const handleSettingsTooltipVisibleChange = useCallback((visible) => {
    setSettingsTooltipVisible(visible);
  }, []);

  useEffect(() => {
    if (!settingsTooltipVisible) {
      return undefined;
    }

    const scrollContainer = getQuestionnairePopupContainer();
    const hideSettingsTooltip = () => {
      setSettingsTooltipVisible(false);
    };

    // Browsers do not fire mouseleave when the trigger scrolls out from under
    // a stationary cursor, so hide as soon as the questionnaire moves.
    scrollContainer.addEventListener('scroll', hideSettingsTooltip, {
      passive: true,
      capture: true,
    });

    return () => {
      scrollContainer.removeEventListener('scroll', hideSettingsTooltip, {
        capture: true,
      });
    };
  }, [settingsTooltipVisible]);

  const isHorizontalReorder = reorderDirection === 'horizontal';

  const removeAnswerMessage = useMemo(() => {
    if (removeAnswerMessageProp) {
      return removeAnswerMessageProp;
    }

    return questionnaireMessages.questionAnswerDropdown.removeAnswer;
  }, [removeAnswerMessageProp]);

  const removeNodeMessage = useMemo(() => {
    if (removeNodeMessageProp) {
      return removeNodeMessageProp;
    }

    return questionnaireMessages.questionAnswerDropdown.removeNode;
  }, [removeNodeMessageProp]);

  useEffect(() => {
    if (!dropdownOpen && !pendingActivateOpen) {
      scrollDismissAnchorRef.current = null;
      return undefined;
    }

    if (dropdownOpen) {
      dropdownOpenedAtRef.current = Date.now();
      refreshScrollDismissAnchor();
    }

    const scrollContainer = getQuestionnairePopupContainer();
    let rafId = null;
    let scrollSettleTimeoutId = null;

    const realignToTrigger = () => {
      if (!dropdownOpen) {
        rafId = null;
        return;
      }

      // rc-trigger re-aligns on window resize; rAF keeps the portaled menu
      // glued to the trigger during scroll.
      window.dispatchEvent(new Event('resize'));
      rafId = null;
    };

    const handleLayoutSettled = () => {
      if (dropdownOpen) {
        applyMenuPlacementFromViewport();
      }
      scheduleActivateScrollGuardRelease();
    };

    const tryScrollDismiss = () => {
      if (
        !dropdownOpen ||
        blockExternalCloseRef.current ||
        pendingGuardReleaseRef.current ||
        pendingActivateOpenRef.current
      ) {
        return;
      }

      const armedForDismiss =
        Date.now() - dropdownOpenedAtRef.current >=
        DROPDOWN_SCROLL_DISMISS_ARM_DELAY_MS;

      if (!armedForDismiss) {
        return;
      }

      if (
        shouldDismissOnScrollAway(
          scrollDismissAnchorRef.current,
          dropdownTriggerRef.current,
          scrollContainer,
        )
      ) {
        closeDropdown();
      }
    };

    const handleScrollSettled = () => {
      handleLayoutSettled();
      tryScrollDismiss();
    };

    const handleScroll = () => {
      if (rafId == null) {
        rafId = requestAnimationFrame(realignToTrigger);
      }

      window.clearTimeout(scrollSettleTimeoutId);
      scrollSettleTimeoutId = window.setTimeout(
        handleScrollSettled,
        DROPDOWN_SCROLL_SETTLE_MS,
      );
    };

    scrollContainer.addEventListener('scroll', handleScroll, {
      passive: true,
    });

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            if (rafId == null) {
              rafId = requestAnimationFrame(realignToTrigger);
            }

            window.clearTimeout(scrollSettleTimeoutId);
            scrollSettleTimeoutId = window.setTimeout(
              handleLayoutSettled,
              DROPDOWN_SCROLL_SETTLE_MS,
            );
          })
        : null;
    resizeObserver?.observe(scrollContainer);

    return () => {
      if (rafId != null) {
        cancelAnimationFrame(rafId);
      }
      window.clearTimeout(scrollSettleTimeoutId);
      scrollContainer.removeEventListener('scroll', handleScroll);
      resizeObserver?.disconnect();
      scrollDismissAnchorRef.current = null;
    };
  }, [
    dropdownOpen,
    pendingActivateOpen,
    closeDropdown,
    applyMenuPlacementFromViewport,
    scheduleActivateScrollGuardRelease,
    refreshScrollDismissAnchor,
  ]);

  useEffect(() => () => clearActivateScrollGuard(), [clearActivateScrollGuard]);

  useLayoutEffect(() => {
    const placementChanged = prevMenuPlacementRef.current !== menuPlacement;
    prevMenuPlacementRef.current = menuPlacement;

    if (!dropdownOpen) {
      return;
    }

    if (!placementChanged && !pendingGuardReleaseRef.current) {
      return;
    }

    window.requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
      window.requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'));
        if (pendingGuardReleaseRef.current) {
          completeActivateScrollGuard();
        }
      });
    });
  }, [dropdownOpen, menuPlacement, completeActivateScrollGuard]);

  const stopPropagation = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const {
    startStreamingMessage: createAIChatMessage,
    setUserPromptWithOptions: createAIChatMessageWithOptions,
    toggleOpen: toggleAIChatOpen,
    isSurveySaving,
  } = useAiSurveyBuilder();
  const hasPermissionToUseAI = useHasPermission(EDIT_PERMISSIONS.AI_BUILDER);
  const hasPermissionToSeeAIBuilder = useHasPermission(
    VISIBILITY_PERMISSIONS.AI_BUILDER,
  );

  const { goToLogic } = useNavigation({ surveyId });

  const canShowAdvancedLogic =
    permissions[EDIT_PERMISSIONS.SETTING_ADVANCED_LOGIC];

  const isQuestion =
    nodeRefId === refId && valueType === ActionVisibilityValueType.Node;

  const owningQuestionRefId = isQuestion
    ? refId
    : currentQuestionRefId || nodeRefId;
  const owningGroupRefId = parentGroup?.refId;

  const activateOwningQuestion = useCallback(() => {
    if (!owningQuestionRefId) {
      return;
    }

    dispatch(
      setActiveNode({
        refId: owningQuestionRefId,
        groupRefId: owningGroupRefId,
      }),
    );
  }, [dispatch, owningQuestionRefId, owningGroupRefId]);

  const handleDropdownOpenChange = useCallback(
    (open) => {
      if (!open && keepDropdownOpenRef.current) {
        keepDropdownOpenRef.current = false;
        return;
      }

      if (
        !open &&
        (blockExternalCloseRef.current ||
          pendingGuardReleaseRef.current ||
          pendingActivateOpenRef.current)
      ) {
        if (pendingActivateOpenRef.current) {
          clearActivateScrollGuard();
        }
        return;
      }

      if (open) {
        if (owningQuestionRefId && owningQuestionRefId !== activeQuestionId) {
          // Defer opening until activate scroll settles so placement is computed
          // once at the final trigger position (avoids top/bottom flip while open).
          pendingActivateOpenRef.current = true;
          setPendingActivateOpen(true);
          startActivateScrollGuard();
          activateOwningQuestion();
          setSettingsTooltipVisible(false);
          return;
        }

        clearActivateScrollGuard();
        dropdownOpenedAtRef.current = Date.now();
        applyMenuPlacementFromViewport();
      } else {
        clearActivateScrollGuard();
      }

      setDropdownOpen(open);
      setSettingsTooltipVisible(false);
    },
    [
      activateOwningQuestion,
      owningQuestionRefId,
      activeQuestionId,
      applyMenuPlacementFromViewport,
      startActivateScrollGuard,
      clearActivateScrollGuard,
    ],
  );

  const { isSet: isSetDisplayLogic } = useStatePropsAndActionsVAB({
    refId,
    nodeRefId: currentQuestionRefId || nodeRefId || refId,
    valueType,
    quotaMode,
  });

  const { isSet: isSetNavigationLogic, onNavigate: onNavigateNavigationLogic } =
    useNavigationLogicState(refId, nodeType);

  const hasNavigationLogicPermission = useHasPermission(
    NAVIGATION_PERMISSIONS.ADVANCED_LOGIC_TAB,
  );
  const hasAdvancedLogicEditPermission = useHasPermission(
    EDIT_PERMISSIONS.SETTING_ADVANCED_LOGIC,
  );
  const hasReorderPermission = useHasPermission(
    EDIT_PERMISSIONS.ANSWER_REORDER,
  );

  const unconditionalHideItemToCheck = useMemo(
    () => ({
      id,
      refId,
      questionRefId: currentQuestionRefId || nodeRefId,
      valueType,
    }),
    [id, refId, currentQuestionRefId, nodeRefId, valueType],
  );
  const {
    hasPermission: canShowUnconditionalHideAnswerAction,
    permissionValues: unconditionalHidePermissions,
  } = useHasPermissionWithValues(
    [
      VISIBILITY_PERMISSIONS.SHOW_UNCONDITIONAL_HIDE_ANSWER_BUTTON,
      VISIBILITY_PERMISSIONS.SHOW_UNCONDITIONAL_HIDE_ANSWER_RESTORE_BUTTON,
    ],
    unconditionalHideItemToCheck,
  );
  const canShowUnconditionalHideAnswerRestore =
    unconditionalHidePermissions[
      VISIBILITY_PERMISSIONS.SHOW_UNCONDITIONAL_HIDE_ANSWER_RESTORE_BUTTON
    ];
  const { hasHideLogic } = useUnconditionalHideState({
    nodeRefId: currentQuestionRefId || nodeRefId,
    valueRefId: refId,
    valueType,
  });
  const hiddenNodes = useSelector(makeSelectHiddenNodes);
  const isNodeHidden = hiddenNodes.includes(refId);
  const unconditionalHideNodeItemToCheck = useMemo(
    () => ({
      id,
      refId,
      isScreening,
      inVanWestendorpGroup,
    }),
    [id, refId, isScreening, inVanWestendorpGroup],
  );
  const canShowUnconditionalHideNode = useHasPermission(
    VISIBILITY_PERMISSIONS.SHOW_HIDE_NODE_BUTTON,
    unconditionalHideNodeItemToCheck,
  );
  const { mutate: mutateFavorite } = useMutateFavoriteQuestion({
    onSuccess: ({ isFavorite: nextIsFavorite }) => {
      dispatch(
        updateQuestionFavoriteState({
          questionRefId: refId,
          isFavorite: nextIsFavorite,
        }),
      );
    },
  });
  const removeFavoriteFromIndicator = useCallback(() => {
    if (!refId || !surveyId) {
      return;
    }

    dispatch(
      updateQuestionFavoriteState({
        questionRefId: refId,
        isFavorite: false,
      }),
    );
    mutateFavorite({
      favorite: false,
      questionRefId: refId,
      surveyId: parseInt(surveyId, 10),
    });
  }, [dispatch, mutateFavorite, refId, surveyId]);
  const removeExclusiveFromIndicator = useCallback(() => {
    const toggleExclusive = toggleExclusiveAnswer || toggleExclusiveScorepoint;
    toggleExclusive?.(refId);
  }, [toggleExclusiveAnswer, toggleExclusiveScorepoint, refId]);

  const navigationLogicDisabled =
    !hasNavigationLogicPermission ||
    !hasAdvancedLogicEditPermission ||
    inShuffledGroup;

  const displayLogicPermission =
    valueType === ActionVisibilityValueType.Answer
      ? EDIT_PERMISSIONS.ANSWER_VISIBILITY
      : EDIT_PERMISSIONS.SETTING_DISPLAY_LOGIC;
  const hasDisplayLogicPermission = useHasPermission(displayLogicPermission);
  const hasDisplayLogicTabPermission = useHasPermission(
    NAVIGATION_PERMISSIONS.DISPLAY_LOGIC_TAB,
  );
  const { isEnabled: isDisplayLogicEnabled, cannotSet: cannotSetDisplayLogic } =
    useStatePropsAndActionsVAB({
      refId,
      nodeRefId: currentQuestionRefId || nodeRefId || refId,
      valueType,
      quotaMode,
    });
  const { goToDisplayLogic } = useNavigation({ surveyId });
  const onNavigateDisplayLogic = useCallback(() => {
    activateOwningQuestion();

    goToDisplayLogic({
      nodeRefId: currentQuestionRefId || nodeRefId || refId,
      refId,
      valueType,
    });

    if (!isSetDisplayLogic) {
      dispatch(
        addStatement(
          getDefaultStatementWithOutcomeAction({
            nodeRefId: currentQuestionRefId || nodeRefId || refId,
            valueRefId: refId,
            valueType,
            scrollTo: true,
          }),
        ),
      );
    } else {
      dispatch(
        setScrollToStatementId({
          nodeId: currentQuestionRefId || nodeRefId || refId,
          valueId: refId,
          valueType,
        }),
      );
    }
    closeDropdown();
  }, [
    activateOwningQuestion,
    goToDisplayLogic,
    refId,
    currentQuestionRefId,
    nodeRefId,
    valueType,
    isSetDisplayLogic,
    dispatch,
    closeDropdown,
  ]);
  const displayLogicDisabled =
    !isDisplayLogicEnabled ||
    !hasDisplayLogicTabPermission ||
    isVisibilityActionButtonDisabled({
      hasPermission: hasDisplayLogicPermission,
      isDisabled: disabled,
      cannotSet: cannotSetDisplayLogic,
      inShuffledGroup,
      quotaMode,
      randomizeQuestionnaireEnabled,
    });

  const hasAction = useCallback(
    (action) => supportedActions.includes(action),
    [supportedActions],
  );

  const hasMaxDiffDesignActions =
    hasAction(QA_ACTIONS.GENERATE_MAXDIFF_DESIGN) ||
    hasAction(QA_ACTIONS.DOWNLOAD_MAXDIFF_DESIGN);

  const {
    generateDesign,
    downloadDesign,
    generateStatus,
    isDownloading,
    isDesignStale,
    isDownloadEnabled,
    showStaleIndicator,
  } = useMaxDiffExperimentalDesign({
    surveyId,
    refId,
    enabled: hasMaxDiffDesignActions,
  });

  const resolvedUploadMediaPermission =
    uploadMediaPermission ||
    (valueType === ActionVisibilityValueType.Node
      ? EDIT_PERMISSIONS.SETTING_MEDIA
      : EDIT_PERMISSIONS.ANSWER_EDIT_MEDIA);

  const canShowUploadMediaAction =
    hasAction(QA_ACTIONS.UPLOAD_MEDIA) &&
    canModifyMedia &&
    (canUpsertMedia ?? Boolean(uploadUrl && onAddMedia));

  const showAnswerRestoreAsOnlyDropdownAction =
    hasAction(QA_ACTIONS.UNCONDITIONAL_HIDE) &&
    canShowUnconditionalHideAnswerRestore &&
    hasHideLogic;
  const showNodeRestoreAsOnlyDropdownAction =
    (hasAction(QA_ACTIONS.REMOVE_QUESTION) ||
      hasAction(QA_ACTIONS.REMOVE_NODE)) &&
    canShowUnconditionalHideNode &&
    isNodeHidden &&
    !inVanWestendorpGroup &&
    !isParentGroupNodeHidden;
  const showRestoreAsOnlyDropdownAction =
    showAnswerRestoreAsOnlyDropdownAction ||
    showNodeRestoreAsOnlyDropdownAction;
  const canShowMediaSlotsAction =
    hasAction(QA_ACTIONS.UPLOAD_MEDIA) &&
    canModifyMedia &&
    Boolean(mediaSlots?.length);

  const menuActions = useMemo(() => {
    if (showRestoreAsOnlyDropdownAction) {
      return [
        {
          stayAfterLogicGroup: true,
          onClick: closeDropdown,
          icon: null,
          text: showNodeRestoreAsOnlyDropdownAction ? (
            <UnconditionalHideNode
              nodeRefId={refId}
              nodeType={nodeType}
              valueType={valueType || ActionVisibilityValueType.Node}
              iconHide={removeNodeIcon || 'folder-delete'}
              iconSize={14}
              iconClassesHide="text-size-12"
              showHideDivider={false}
              type={QA_ACTION_TYPES.BUTTON_WITH_TEXT}
            />
          ) : (
            <UnconditionalHideAnswer
              nodeRefId={currentQuestionRefId || nodeRefId}
              valueRefId={refId}
              valueType={valueType}
              type={QA_ACTION_TYPES.BUTTON_WITH_TEXT}
            />
          ),
        },
      ];
    }

    const actions = [];

    const pushTailDivider = () => {
      const lastAction = actions[actions.length - 1];
      if (actions.length > 0 && lastAction?.type !== 'divider') {
        actions.push({
          show: true,
          type: 'divider',
          stayAfterLogicGroup: true,
        });
      }
    };

    if (canShowMediaSlotsAction) {
      mediaSlots.forEach((slot) => {
        actions.push({
          icon: null,
          text: (
            <WithPermissions
              permission={slot.permission || resolvedUploadMediaPermission}
            >
              {(hasPermission) => (
                <UploadMedia
                  uploadUrl={slot.uploadUrl}
                  supportedMediaTypes={supportedMediaTypes}
                  mediaValue={slot.media}
                  altText="preview media"
                  onAdd={slot.onAdd}
                  onRemove={slot.onRemove}
                  disabled={disabled || !hasPermission}
                  type={QA_ACTION_TYPES.BUTTON_WITH_TEXT}
                  onOpen={closeDropdown}
                  addMessage={slot.addMessage}
                  editMessage={slot.editMessage}
                />
              )}
            </WithPermissions>
          ),
        });
      });
    } else if (canShowUploadMediaAction) {
      actions.push({
        icon: null,
        text: (
          <WithPermissions permission={resolvedUploadMediaPermission}>
            {(hasPermission) => (
              <UploadMedia
                uploadUrl={uploadUrl}
                supportedMediaTypes={supportedMediaTypes}
                mediaValue={media}
                altText="preview media"
                onAdd={onAddMedia}
                onRemove={onRemoveMedia}
                disabled={disabled || !hasPermission}
                type={QA_ACTION_TYPES.BUTTON_WITH_TEXT}
                onOpen={closeDropdown}
              />
            )}
          </WithPermissions>
        ),
      });
    }

    if (hasAction(QA_ACTIONS.NAVIGATION_LOGIC)) {
      actions.push({
        isLogicAction: true,
        onClick: closeDropdown,
        icon: null,
        text: (
          <ListItemButton
            disabled={navigationLogicDisabled}
            onClick={() => {
              activateOwningQuestion();
              onNavigateNavigationLogic();
              closeDropdown();
            }}
            data-testid="navigation-logic-btn"
            icon="advanced-logic"
            iconSize={16}
            text={
              <FormattedMessage
                {...(isSetNavigationLogic
                  ? questionnaireMessages.questionAnswerDropdown
                      .editAdvancedLogic
                  : questionnaireMessages.advancedLogic)}
              />
            }
            style={{ top: 3 }}
          />
        ),
        disabled: navigationLogicDisabled,
      });
    }

    if (
      hasAction(QA_ACTIONS.ADVANCED_LOGIC) &&
      canShowAdvancedLogic &&
      isQuestion
    ) {
      actions.push({
        onClick: closeDropdown,
        icon: null,
        text: (
          <ListItemButton
            onClick={() => {
              activateOwningQuestion();
              goToLogic();
              closeDropdown();
            }}
            data-testid="advanced-logic-btn"
            icon="advanced-logic"
            iconSize={16}
            text={<FormattedMessage {...questionnaireMessages.advancedLogic} />}
          />
        ),
      });
    }

    if (hasAction(QA_ACTIONS.EXCLUSIVE) && supportsExclusiveAnswerUI) {
      actions.push({
        onClick: closeDropdown,
        icon: null,
        text: (
          <ExclusiveButton
            isExclusive={isExclusive}
            toggleExclusiveAnswer={
              toggleExclusiveAnswer || toggleExclusiveScorepoint
            }
            answerRefId={refId}
            type={QA_ACTION_TYPES.BUTTON_WITH_TEXT}
          />
        ),
      });
    }

    if (hasAction(QA_ACTIONS.AI_ACTIONS)) {
      const canShowAiActions =
        hasPermissionToSeeAIBuilder && Boolean(props.showAi ?? true);

      if (canShowAiActions) {
        const aiType = isQuestion ? 'question' : 'answer';
        const questionIndex = props.questionIndex ?? index;
        const questionType = props.questionType ?? props.type;
        const availableAiActions = getAvailableDropdownAIActions(
          aiType,
          questionIndex,
          index,
          getAiActionsExcludeList(aiType, questionType),
          isScreening,
          props.answerType,
        );
        const areActionsDisabled =
          !hasPermissionToUseAI ||
          isSurveySaving ||
          !hasPermissionToSeeAIBuilder;

        if (availableAiActions.length > 0) {
          actions.push({
            type: 'submenu',
            className: 'full-size-link',
            stayAfterLogicGroup: true,
            dividerBefore: true,
            dividerAfter: true,
            alignSubmenuWithParentMenu: true,
            useAiActionsMenu: true,
            text: (
              <ListItemButton
                disabled={areActionsDisabled}
                data-testid="ai-actions-btn"
                icon="ai-chat-bubble"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                iconSize={16}
                text={
                  <FormattedMessage
                    {...questionnaireMessages.questionAnswerDropdown.aiActions}
                  />
                }
              />
            ),
            disabled: areActionsDisabled,
            submenuActions: buildAiDropdownActions({
              availableActions: availableAiActions,
              disabled: areActionsDisabled,
              onActionClick: ({
                aiMessageType,
                aiMessageTypeWithoutOption,
                options,
              }) => {
                if (options.length) {
                  createAIChatMessageWithOptions(
                    aiMessageType,
                    aiMessageTypeWithoutOption,
                    options,
                  );
                } else {
                  createAIChatMessage(aiMessageType);
                }
                closeDropdown();
                toggleAIChatOpen(true);
              },
            }),
          });
        }
      }
    }

    if (
      hasAction(QA_ACTIONS.DISPLAY_LOGIC) &&
      canShowDisplayLogic &&
      isDisplayLogicEnabled
    ) {
      actions.push({
        isLogicAction: true,
        onClick: closeDropdown,
        icon: null,
        text: (
          <ListItemButton
            disabled={displayLogicDisabled}
            onClick={onNavigateDisplayLogic}
            data-testid="display-logic-btn"
            icon="answer-logic"
            iconSize={16}
            text={
              <FormattedMessage
                {...(isSetDisplayLogic
                  ? questionnaireMessages.questionAnswerDropdown
                      .editDisplayLogic
                  : questionnaireMessages.questionAnswerDropdown
                      .applyDisplayLogic)}
              />
            }
          />
        ),
        disabled: displayLogicDisabled,
      });
    }

    if (hasAction(QA_ACTIONS.FAVORITE) && showFavorite) {
      actions.push({
        onClick: closeDropdown,
        icon: null,
        text: (
          <FavoriteCheckbox
            questionRefId={refId}
            surveyId={surveyId}
            defaultChecked={isFavorite}
            type={QA_ACTION_TYPES.BUTTON_WITH_TEXT}
            onToggle={closeDropdown}
          />
        ),
      });
    }

    if (hasMaxDiffDesignActions) {
      const isGeneratingDesign =
        generateStatus === GENERATE_DESIGN_STATUS.LOADING;
      const isDesignCreated =
        generateStatus === GENERATE_DESIGN_STATUS.SUCCESS && !isDesignStale;

      // Omit generate when a current design already exists.
      if (!isDesignCreated) {
        const generateDesignLabel = (() => {
          if (isGeneratingDesign) {
            return questionnaireMessages.questionAnswerDropdown.creatingDesign;
          }

          if (isDesignStale) {
            return questionnaireMessages.questionAnswerDropdown.updateDesign;
          }

          return questionnaireMessages.questionAnswerDropdown.generateDesign;
        })();

        const generateDesignIcon = isGeneratingDesign ? (
          <span
            className="display-inline-block margin-right-xxs position-relative text-align-center"
            style={{ width: 16, height: 16, textAlign: 'center' }}
          >
            <span
              className="position-absolute"
              style={{
                transform: 'scale(0.22)',
                transformOrigin: 'top left',
                top: 0,
                left: 0,
              }}
            >
              <PfLoader />
            </span>
          </span>
        ) : (
          <Pficon
            className="text-size-secondary margin-right-xxs text-align-center"
            style={ICON_BOX_STYLE}
            size={16}
            type="create-design"
          />
        );

        actions.push({
          stayAfterLogicGroup: true,
          onClick: () => {
            keepDropdownOpenRef.current = true;
          },
          icon: null,
          text: (
            <ListItemButton
              disabled={isGeneratingDesign}
              onClick={() => {
                keepDropdownOpenRef.current = true;
                generateDesign();
              }}
              data-testid="generate-design-btn"
              icon={null}
              text={
                <span className="flex flex-items-center">
                  {generateDesignIcon}
                  <FormattedMessage {...generateDesignLabel} />
                </span>
              }
            />
          ),
          disabled: isGeneratingDesign,
        });
      }

      actions.push({
        stayAfterLogicGroup: true,
        onClick: closeDropdown,
        icon: null,
        text: (
          <ListItemButton
            disabled={!isDownloadEnabled || isDownloading}
            onClick={() => {
              downloadDesign();
              closeDropdown();
            }}
            data-testid="download-design-btn"
            icon="survey-download"
            text={
              <FormattedMessage
                {...questionnaireMessages.questionAnswerDropdown.downloadDesign}
              />
            }
          />
        ),
        disabled: !isDownloadEnabled || isDownloading,
      });
    }

    if (
      (hasAction(QA_ACTIONS.REORDER_UP) ||
        hasAction(QA_ACTIONS.REORDER_DOWN)) &&
      canReorder &&
      hasReorderPermission
    ) {
      if (hasAction(QA_ACTIONS.REORDER_UP)) {
        actions.push({
          stayAfterLogicGroup: true,
          onClick: closeDropdown,
          icon: null,
          text: (
            <ListItemButton
              disabled={!canReorderAnswerUp}
              onClick={() => {
                reorderAnswerUp({ refId });
                closeDropdown();
              }}
              data-testid="move-up-btn-answer"
              icon={isHorizontalReorder ? 'arrowleft2' : 'arrowup2'}
              text={
                <FormattedMessage
                  {...(isHorizontalReorder
                    ? questionnaireMessages.questionAnswerDropdown.moveLeft
                    : questionnaireMessages.questionAnswerDropdown.moveUp)}
                />
              }
            />
          ),
          disabled: !canReorderAnswerUp,
        });
      }

      if (hasAction(QA_ACTIONS.REORDER_DOWN)) {
        actions.push({
          stayAfterLogicGroup: true,
          onClick: closeDropdown,
          icon: null,
          text: (
            <ListItemButton
              disabled={!canReorderAnswerDown}
              onClick={() => {
                reorderAnswerDown({ refId });
                closeDropdown();
              }}
              data-testid="move-down-btn-answer"
              icon={isHorizontalReorder ? 'arrowright2' : 'arrowdown2'}
              text={
                <FormattedMessage
                  {...(isHorizontalReorder
                    ? questionnaireMessages.questionAnswerDropdown.moveRight
                    : questionnaireMessages.questionAnswerDropdown.moveDown)}
                />
              }
            />
          ),
          disabled: !canReorderAnswerDown,
        });
      }
    }

    if (
      hasAction(QA_ACTIONS.UNCONDITIONAL_HIDE) &&
      canShowUnconditionalHideAnswerAction
    ) {
      pushTailDivider();
      actions.push({
        stayAfterLogicGroup: true,
        onClick: closeDropdown,
        icon: null,
        text: (
          <UnconditionalHideAnswer
            nodeRefId={currentQuestionRefId || nodeRefId}
            valueRefId={refId}
            valueType={valueType}
            type={QA_ACTION_TYPES.BUTTON_WITH_TEXT}
          />
        ),
      });
    }

    if (
      hasAction(QA_ACTIONS.REMOVE_ANSWER) &&
      !(
        hasAction(QA_ACTIONS.UNCONDITIONAL_HIDE) &&
        canShowUnconditionalHideAnswerAction
      ) &&
      removeAnswer
    ) {
      pushTailDivider();
      actions.push({
        stayAfterLogicGroup: true,
        onClick: closeDropdown,
        icon: null,
        text: (
          <WithPermissions
            permission={EDIT_PERMISSIONS.ANSWER_DELETE}
            itemToCheck={{ id, refId }}
          >
            {(hasDeleteAnswerPermission) => (
              <ListItemButton
                disabled={!canRemove || !hasDeleteAnswerPermission}
                onClick={() => {
                  removeAnswer({ refId });
                  closeDropdown();
                }}
                icon="close"
                iconSize={16}
                data-testid="remove-btn-answer"
                text={<FormattedMessage {...removeAnswerMessage} />}
              />
            )}
          </WithPermissions>
        ),
        disabled: !canRemove,
      });
    }

    if (hasAction(QA_ACTIONS.REMOVE_NODE) && canShowRemove && onRemoveNode) {
      pushTailDivider();
      actions.push({
        stayAfterLogicGroup: true,
        onClick: closeDropdown,
        icon: null,
        text: (
          <WithPermissions
            permission={[
              VISIBILITY_PERMISSIONS.SHOW_HIDE_NODE_BUTTON,
              EDIT_PERMISSIONS.QUESTION_DELETE,
            ]}
            itemToCheck={{
              id,
              refId,
              isScreening,
              inVanWestendorpGroup,
            }}
          >
            {(_, [canShowUnConditionalHide, hasDeletePermission]) => {
              if (canShowUnConditionalHide) {
                return (
                  <UnconditionalHideNode
                    nodeRefId={refId}
                    nodeType={nodeType}
                    valueType={valueType || ActionVisibilityValueType.Node}
                    iconHide={removeNodeIcon || 'folder-delete'}
                    iconSize={14}
                    iconClassesHide="text-size-12"
                    showHideDivider={false}
                    type={QA_ACTION_TYPES.BUTTON_WITH_TEXT}
                  />
                );
              }

              return (
                <ListItemButton
                  disabled={removeNodeDisabled || !hasDeletePermission}
                  onClick={() => {
                    onRemoveNode(refId);
                    closeDropdown();
                  }}
                  data-testid="delete-node-btn"
                  icon={removeNodeIcon}
                  text={<FormattedMessage {...removeNodeMessage} />}
                />
              );
            }}
          </WithPermissions>
        ),
        disabled: removeNodeDisabled,
      });
    }

    if (
      hasAction(QA_ACTIONS.REMOVE_ATTRIBUTE) &&
      canShowRemove &&
      onRemoveNode
    ) {
      pushTailDivider();
      actions.push({
        stayAfterLogicGroup: true,
        onClick: closeDropdown,
        icon: null,
        text: (
          <ListItemButton
            disabled={removeNodeDisabled}
            onClick={() => {
              onRemoveNode(refId);
              closeDropdown();
            }}
            data-testid="delete-attribute-btn"
            icon="folder-delete"
            text={
              <FormattedMessage
                {...questionnaireMessages.questionAnswerDropdown
                  .removeAttribute}
              />
            }
          />
        ),
        disabled: removeNodeDisabled,
      });
    }

    if (
      hasAction(QA_ACTIONS.REMOVE_QUESTION) &&
      canShowRemove &&
      !inVanWestendorpGroup &&
      !isParentGroupNodeHidden
    ) {
      pushTailDivider();
      actions.push({
        stayAfterLogicGroup: true,
        onClick: closeDropdown,
        icon: null,
        text: (
          <WithPermissions
            permission={[
              VISIBILITY_PERMISSIONS.SHOW_HIDE_NODE_BUTTON,
              EDIT_PERMISSIONS.QUESTION_DELETE,
            ]}
            itemToCheck={{
              id,
              refId,
              isScreening,
              inVanWestendorpGroup,
            }}
          >
            {(_, [canShowUnConditionalHide, hasDeletePermission]) => {
              if (inVanWestendorpGroup || isParentGroupNodeHidden) {
                return null;
              }

              if (canShowUnConditionalHide) {
                return (
                  <UnconditionalHideNode
                    nodeRefId={refId}
                    nodeType={nodeType}
                    valueType={ActionVisibilityValueType.Node}
                    iconHide="folder-delete"
                    iconSize={14}
                    iconClassesHide="text-size-12"
                    showHideDivider={false}
                    type={QA_ACTION_TYPES.BUTTON_WITH_TEXT}
                  />
                );
              }

              return (
                <ListItemButton
                  disabled={!hasDeletePermission || canDelete === false}
                  onClick={() => {
                    onRemoveQuestion?.(refId);
                    closeDropdown();
                  }}
                  data-testid="delete-question-btn"
                  icon="folder-delete"
                  text={
                    <FormattedMessage
                      {...questionnaireMessages.questionAnswerDropdown
                        .removeQuestion}
                    />
                  }
                />
              );
            }}
          </WithPermissions>
        ),
      });
    }

    return actions;
  }, [
    hasAction,
    canShowAdvancedLogic,
    isQuestion,
    goToLogic,
    supportsExclusiveAnswerUI,
    isExclusive,
    toggleExclusiveAnswer,
    toggleExclusiveScorepoint,
    canShowDisplayLogic,
    disabled,
    surveyId,
    refId,
    currentQuestionRefId,
    nodeRefId,
    valueType,
    resolvedUploadMediaPermission,
    canShowUploadMediaAction,
    canShowMediaSlotsAction,
    mediaSlots,
    supportedMediaTypes,
    media,
    onAddMedia,
    onRemoveMedia,
    canReorder,
    canReorderAnswerUp,
    canReorderAnswerDown,
    reorderAnswerUp,
    reorderAnswerDown,
    isHorizontalReorder,
    removeAnswerMessage,
    removeNodeMessage,
    removeNodeIcon,
    canShowRemove,
    removeNodeDisabled,
    onRemoveNode,
    id,
    isScreening,
    inVanWestendorpGroup,
    isParentGroupNodeHidden,
    onRemoveQuestion,
    canDelete,
    nodeType,
    isSetNavigationLogic,
    navigationLogicDisabled,
    hasReorderPermission,
    canShowUnconditionalHideAnswerAction,
    showNodeRestoreAsOnlyDropdownAction,
    showRestoreAsOnlyDropdownAction,
    removeAnswer,
    canRemove,
    onNavigateNavigationLogic,
    activateOwningQuestion,
    isDisplayLogicEnabled,
    displayLogicDisabled,
    onNavigateDisplayLogic,
    isSetDisplayLogic,
    showFavorite,
    isFavorite,
    createAIChatMessage,
    createAIChatMessageWithOptions,
    toggleAIChatOpen,
    closeDropdown,
    hasPermissionToSeeAIBuilder,
    hasPermissionToUseAI,
    isSurveySaving,
    index,
    props.questionIndex,
    props.questionType,
    props.type,
    props.answerType,
    props.showAi,
    hasMaxDiffDesignActions,
    generateStatus,
    isDesignStale,
    isDownloadEnabled,
    isDownloading,
    generateDesign,
    downloadDesign,
    uploadUrl,
  ]);

  menuHeightEstimateRef.current = estimateQaDropdownMenuHeight(menuActions);

  const indicatorCount = useMemo(() => {
    let count = 0;
    if (hasAction(QA_ACTIONS.NAVIGATION_LOGIC) && isSetNavigationLogic) {
      count += 1;
    }
    if (
      hasAction(QA_ACTIONS.DISPLAY_LOGIC) &&
      canShowDisplayLogic &&
      isSetDisplayLogic
    ) {
      count += 1;
    }
    if (
      hasAction(QA_ACTIONS.EXCLUSIVE) &&
      supportsExclusiveAnswerUI &&
      isExclusive
    ) {
      count += 1;
    }
    if (hasAction(QA_ACTIONS.UPLOAD_MEDIA) && canModifyMedia && media?.url) {
      count += 1;
    }
    if (hasAction(QA_ACTIONS.FAVORITE) && showFavorite && isFavorite) {
      count += 1;
    }
    return count;
  }, [
    hasAction,
    isSetNavigationLogic,
    canShowDisplayLogic,
    isSetDisplayLogic,
    supportsExclusiveAnswerUI,
    isExclusive,
    canModifyMedia,
    media,
    showFavorite,
    isFavorite,
  ]);

  // Restore-only hides indicators, but the answer may still have logic/media
  // flags. Count only what is actually rendered so the handle keeps the same
  // reserved-slot offset as visible answers.
  const visibleIndicatorCount = showRestoreAsOnlyDropdownAction
    ? 0
    : indicatorCount;
  const showIndicatorArea = visibleIndicatorCount > 0;

  const reservedSlotCount = indicatorAreaMinWidth || 0;
  let indicatorAreaWidth;
  if (showIndicatorArea) {
    const alignedSlotCount =
      reservedSlotCount > 0
        ? Math.max(visibleIndicatorCount, reservedSlotCount)
        : visibleIndicatorCount;
    indicatorAreaWidth = getIndicatorAreaWidth(alignedSlotCount);
  }
  // Matrix actions cells center their content; reserved-slot offset fights that.
  const dropdownAlignmentMarginLeft =
    !isMatrixCell &&
    !isCenteredCell &&
    !showIndicatorArea &&
    reservedSlotCount > visibleIndicatorCount
      ? getIndicatorAreaWidth(reservedSlotCount - visibleIndicatorCount) +
        INDICATORS_TO_DROPDOWN_GAP
      : undefined;

  const resolvedActionsAreaMinWidth = (() => {
    if (actionsAreaMinWidth > 0) {
      return actionsAreaMinWidth;
    }

    if (reservedSlotCount > 0) {
      return getActionsAreaMinWidth(reservedSlotCount);
    }

    return 0;
  })();

  const hasMenuActions = menuActions.length > 0;

  const actionsAreaRef = useCallback(
    (el) => {
      registerActionsAreaRef?.(el);
    },
    [registerActionsAreaRef],
  );

  const isMatrixColumnCell =
    isMatrixCell && valueType === ActionVisibilityValueType.Column;

  if (!hasMenuActions && !showIndicatorArea) {
    return null;
  }

  const mediaPreviewMargin = 0;

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      ref={registerActionsAreaRef ? actionsAreaRef : undefined}
      className={classnames(
        'question-answer-dropdown-actions',
        'flex flex-items-center position-relative',
        {
          'matrix-cell-actions': isMatrixCell,
          'flex-justify-end':
            isMatrixColumnCell ||
            (resolvedActionsAreaMinWidth > 0 &&
              !isMatrixCell &&
              !isCenteredCell),
          'flex-justify-center':
            (isMatrixCell && !isMatrixColumnCell) || isCenteredCell,
          // Sit above the hidden-question fade overlay so restore stays clickable.
          'keep-opaque-element': showNodeRestoreAsOnlyDropdownAction,
        },
      )}
      style={{
        ...style,
        paddingLeft:
          quotaMode === QUOTA_MODE.NONE && !isMatrixCell && !isCenteredCell
            ? ACTIONS_FROM_INPUT_GAP
            : undefined,
        minWidth:
          resolvedActionsAreaMinWidth > 0
            ? resolvedActionsAreaMinWidth
            : undefined,
      }}
      onClick={stopPropagation}
      onMouseDown={stopPropagation}
      onFocus={stopPropagation}
    >
      {showIndicatorArea && (
        <div
          className={classnames(
            'flex flex-justify-end flex-align-items--center question-answer-actions',
          )}
          style={{ minWidth: indicatorAreaWidth }}
        >
          {hasAction(QA_ACTIONS.NAVIGATION_LOGIC) && isSetNavigationLogic && (
            <IndicatorWrapper
              onClick={() => {
                activateOwningQuestion();
                onNavigateNavigationLogic();
              }}
              tooltip={
                <FormattedMessage
                  {...questionnaireMessages.questionAnswerDropdown
                    .editAdvancedLogic}
                />
              }
            >
              <Pficon
                type="advanced-logic"
                className="color-grey-10 text-align-center"
                style={TOP_LEVEL_ICON_STYLE}
              />
            </IndicatorWrapper>
          )}

          {hasAction(QA_ACTIONS.DISPLAY_LOGIC) &&
            canShowDisplayLogic &&
            isSetDisplayLogic && (
              <IndicatorWrapper
                onClick={onNavigateDisplayLogic}
                tooltip={
                  <FormattedMessage
                    {...questionnaireMessages.questionAnswerDropdown
                      .editDisplayLogic}
                  />
                }
              >
                <Pficon
                  type="answer-logic"
                  className="color-grey-10 text-align-center"
                  style={TOP_LEVEL_ICON_STYLE}
                />
              </IndicatorWrapper>
            )}

          {hasAction(QA_ACTIONS.FAVORITE) && showFavorite && isFavorite && (
            <IndicatorWrapper
              onClick={removeFavoriteFromIndicator}
              tooltip={
                <FormattedMessage
                  {...questionnaireMessages.questionAnswerDropdown
                    .removeFromFavorites}
                />
              }
            >
              <Pficon
                type="heart"
                className={classnames('color-grey-10', 'text-align-center')}
                style={{
                  ...TOP_LEVEL_ICON_STYLE,
                  fontSize: 16,
                }}
              />
            </IndicatorWrapper>
          )}
          {hasAction(QA_ACTIONS.EXCLUSIVE) &&
            supportsExclusiveAnswerUI &&
            isExclusive && (
              <IndicatorWrapper
                onClick={removeExclusiveFromIndicator}
                tooltip={
                  <FormattedMessage
                    {...questionnaireMessages.questionAnswerDropdown
                      .unmarkAsExclusive}
                  />
                }
              >
                <Pficon
                  type="exclusive"
                  className="color-grey-10 text-align-center position-relative"
                  style={{
                    ...TOP_LEVEL_ICON_STYLE,
                    fontSize: 16,
                  }}
                />
              </IndicatorWrapper>
            )}

          {hasAction(QA_ACTIONS.UPLOAD_MEDIA) &&
            canModifyMedia &&
            media?.url && (
              <span className="question-answer-action-indicator-slot position-relative">
                <UploadMedia
                  uploadUrl={uploadUrl}
                  supportedMediaTypes={supportedMediaTypes}
                  mediaValue={media}
                  altText="preview media"
                  onAdd={onAddMedia}
                  onRemove={onRemoveMedia}
                  disabled={disabled}
                  hideAddButton
                  previewStyle={{
                    margin: mediaPreviewMargin,
                  }}
                />
              </span>
            )}
        </div>
      )}

      {hasMenuActions && (
        <div
          ref={dropdownTriggerRef}
          className={classnames(
            'flex',
            'flex-items-center',
            'position-relative',
          )}
          style={{ marginLeft: dropdownAlignmentMarginLeft }}
        >
          {showStaleIndicator && (
            <span className="orange-indicator margin-right-xxs" />
          )}
          <DropdownActions
            visible={dropdownOpen}
            onChangeStatus={handleDropdownOpenChange}
            menuCloseOnAction
            destroyPopupOnHide={false}
            menuDataTestId={`answer-${index}-dropdown-menu`}
            dropdownIcon={
              <Tooltip
                arrowPointAtCenter
                overlayClassName="tooltip-light"
                trigger="hover"
                mouseLeaveDelay={0}
                visible={!dropdownOpen && settingsTooltipVisible}
                onVisibleChange={handleSettingsTooltipVisibleChange}
                title={
                  <FormattedMessage
                    {...(settingsTooltipMessageProp ||
                      SETTINGS_TOOLTIP_BY_VALUE_TYPE[valueType] ||
                      questionnaireMessages.answerSettings)}
                  />
                }
                getPopupContainer={getQuestionnairePopupContainer}
                placement="bottomRight"
                overlayStyle={{ zIndex: 1100, pointerEvents: 'none' }}
              >
                <Pficon
                  type="more_vert"
                  style={TOP_LEVEL_ICON_STYLE}
                  className="color-grey-10 text-align-center"
                  data-testid={`answer-${index}-dropdown-trigger`}
                />
              </Tooltip>
            }
            dropdownClassName={classnames(
              'border-radius-level-1',
              'cursor-pointer',
              'qa-dropdown-actions',
            )}
            menuPlacement={menuPlacement}
            align={{
              useCssRight: true,
              // Placement is chosen from viewport space and refreshed after
              // scrolling settles; keep it stable during the scroll itself.
              overflow: {
                adjustY: false,
              },
            }}
            menuClassName={classnames(
              'border-radius-level-1',
              'qa-dropdown-menu',
              {
                'qa-dropdown-menu--single-item': menuActions.length === 1,
              },
            )}
            dropdownTrigger={['click']}
            getPopupContainer={getQuestionnairePopupContainer}
            menuGetPopupContainer={getQuestionnairePopupContainer}
            overlayStyle={{ zIndex: 1100 }}
            actions={menuActions}
          />
        </div>
      )}
    </div>
  );
};

const mapStateToProps = createStructuredSelector({});

const withConnect = connect(mapStateToProps);

export default compose(withConnect)(QuestionAnswerDropdownActions);
